'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { INITIAL_MATURITY_DATA, MaturityData, MaturityKey, WeightingMethod, calcInstantAPR } from '@/app/yield-curve-simulator/data/initialData';

interface CurveContextValue {
  maturities: MaturityData[];
  selectedMaturity: MaturityKey;
  setSelectedMaturity: (m: MaturityKey) => void;
  updateMaturity: (updated: MaturityData) => void;
  resetData: () => void;
  exportJSON: () => string;
  importJSON: (json: string) => boolean;
  instantWeighting: WeightingMethod;
  setInstantWeighting: (m: WeightingMethod) => void;
  hiddenMaturities: Set<MaturityKey>;
  toggleMaturityVisibility: (m: MaturityKey) => void;
}

const CurveContext = createContext<CurveContextValue | null>(null);

const STORAGE_KEY = 'spine_curve_data';

/**
 * Ensures the Instant maturity's apr / totalLiquidity / totalBorrowDemand
 * are always derived from its instantMarkets source list.
 * This is the single source-of-truth reconciliation step.
 */
function reconcileInstant(maturities: MaturityData[], weighting: WeightingMethod = 'totalBorrowed'): MaturityData[] {
  return maturities.map((m) => {
    if (m.maturity !== 'Instant') return m;
    const markets = m.instantMarkets && m.instantMarkets.length > 0 ? m.instantMarkets : null;
    if (!markets) return m;
    const derivedAPR = calcInstantAPR(markets, weighting);
    const totalLiquidity = markets.reduce((s, mk) => s + mk.totalSupply, 0);
    const totalBorrowDemand = markets.reduce((s, mk) => s + mk.totalBorrowed, 0);
    return { ...m, apr: derivedAPR, totalLiquidity, totalBorrowDemand };
  });
}

export function CurveProvider({ children }: { children: React.ReactNode }) {
  const [instantWeighting, setInstantWeighting] = useState<WeightingMethod>('totalBorrowed');
  const [hiddenMaturities, setHiddenMaturities] = useState<Set<MaturityKey>>(new Set());

  const [maturities, setMaturities] = useState<MaturityData[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as MaturityData[];
          return reconcileInstant(parsed, 'totalBorrowed');
        }
      } catch {}
    }
    return INITIAL_MATURITY_DATA;
  });

  const [selectedMaturity, setSelectedMaturity] = useState<MaturityKey>('4W');

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(maturities));
    } catch {}
  }, [maturities]);

  // Re-reconcile Instant whenever weighting changes
  useEffect(() => {
    setMaturities((prev) => reconcileInstant(prev, instantWeighting));
  }, [instantWeighting]);

  const updateMaturity = useCallback((updated: MaturityData) => {
    setMaturities((prev) => {
      const next = prev.map((m) => (m.maturity === updated.maturity ? updated : m));
      // Always reconcile Instant after any update
      return reconcileInstant(next, instantWeighting);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantWeighting]);

  const resetData = useCallback(() => {
    setMaturities(INITIAL_MATURITY_DATA);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const exportJSON = useCallback(() => {
    return JSON.stringify(maturities, null, 2);
  }, [maturities]);

  const importJSON = useCallback((json: string): boolean => {
    try {
      const parsed = JSON.parse(json) as MaturityData[];
      if (!Array.isArray(parsed) || parsed.length === 0) return false;
      setMaturities(reconcileInstant(parsed, instantWeighting));
      return true;
    } catch {
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantWeighting]);

  const toggleMaturityVisibility = useCallback((m: MaturityKey) => {
    setHiddenMaturities((prev) => {
      const next = new Set(prev);
      if (next.has(m)) {
        next.delete(m);
      } else {
        next.add(m);
      }
      return next;
    });
  }, []);

  return (
    <CurveContext.Provider
      value={{
        maturities,
        selectedMaturity,
        setSelectedMaturity,
        updateMaturity,
        resetData,
        exportJSON,
        importJSON,
        instantWeighting,
        setInstantWeighting,
        hiddenMaturities,
        toggleMaturityVisibility,
      }}
    >
      {children}
    </CurveContext.Provider>
  );
}

export function useCurve() {
  const ctx = useContext(CurveContext);
  if (!ctx) throw new Error('useCurve must be used within CurveProvider');
  return ctx;
}
